import { canReviewAlertNotifications } from "./userRoles";

/** Aba do hub Guardian com os alertas não respondidos (ADM/ENG). */
export const UNANSWERED_ALERTS_TAB = "unanswered_alerts";

export const SERVER_LOAD_LEVEL = {
  OK: "ok",
  WARNING: "warning",
  CRITICAL: "critical",
};

export const PAUSE_REASON_LABELS = {
  limit_reached: "Limite de envios atingido",
  no_app_recipients: "Nenhum destinatário com o app cadastrado",
  no_area: "Alerta sem área vinculada",
  no_recurrence: "Severidade sem recorrência configurada",
};

/** Motivos de pausa usados nos filtros rápidos da tabela. */
export const PAUSE_REASON_FILTERS = {
  ALL: "all",
  NO_APP: "no_app_recipients",
  LIMIT: "limit_reached",
};

export const PAUSE_REASON_FILTER_LABELS = {
  [PAUSE_REASON_FILTERS.ALL]: "Todos os motivos",
  [PAUSE_REASON_FILTERS.NO_APP]: "Sem destinatário com app",
  [PAUSE_REASON_FILTERS.LIMIT]: "Limite de envios atingido",
};

/**
 * Aviso de carga do login (`guardian_notifications`), só para ADM/ENG e
 * quando o backend pede revisão (ocupação acima do limite configurado).
 */
export function getLoginNotificationAttention(loginResponse) {
  const attention = loginResponse?.guardian_notifications;
  if (!attention?.should_review) return null;
  if (!canReviewAlertNotifications(loginResponse)) return null;
  return attention;
}

/** Destino pós-login quando o servidor está sobrecarregado; `null` mantém o fluxo padrão. */
export function getPostLoginDestination(loginResponse) {
  const attention = getLoginNotificationAttention(loginResponse);
  if (!attention) return null;
  return {
    path: "/guardian",
    state: { activeTab: UNANSWERED_ALERTS_TAB, loginAttention: attention },
  };
}

export function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n)}%`;
}

export function formatSendsProgress(row) {
  const sent = Number(row?.notifications_sent) || 0;
  const max = Number(row?.max_notifications) || 0;
  return max ? `${sent}/${max}` : String(sent);
}

/**
 * Rótulo e tom visual do estado dos envios de um alerta.
 * Tons distintos para facilitar leitura:
 * - active → verde
 * - no_app_recipients → âmbar (configuração / app)
 * - limit_reached → vermelho (esgotou a cota)
 * - demais pausas → cinza
 */
export function describeNotificationStatus(row) {
  if (row?.notifications_active) {
    return { label: "Envios ativos", tone: "active" };
  }
  const reasonKey = row?.notifications_pause_reason;
  const label =
    row?.notifications_pause_reason_label ||
    PAUSE_REASON_LABELS[reasonKey] ||
    "Envios desligados";

  if (reasonKey === "no_app_recipients") {
    return { label, tone: "no_app" };
  }
  if (reasonKey === "limit_reached") {
    return { label, tone: "limit" };
  }
  return { label, tone: "paused" };
}

/** O push só chega a quem tem o app; sem ninguém, reativar só reenvia o e-mail. */
export function hasAppRecipients(row) {
  return Number(row?.recipients_with_app_count) > 0;
}

function normalize(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Opções únicas de projeto (ordenadas), a partir da lista bruta.
 */
export function listUnansweredProjects(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const seen = new Set();
  const projects = [];
  list.forEach((row) => {
    const title = (row?.project_title || "").trim();
    if (!title) return;
    const key = normalize(title);
    if (seen.has(key)) return;
    seen.add(key);
    projects.push(title);
  });
  return projects.sort((a, b) => a.localeCompare(b, "pt-BR"));
}

/**
 * Áreas únicas (ordenadas). Se `project` for informado, restringe ao projeto.
 */
export function listUnansweredAreas(rows, { project = "" } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const projectKey = normalize(project).trim();
  const seen = new Set();
  const areas = [];
  list.forEach((row) => {
    if (projectKey && normalize(row?.project_title) !== projectKey) return;
    const name = (row?.area_name || "").trim();
    if (!name) return;
    const key = normalize(name);
    if (seen.has(key)) return;
    seen.add(key);
    areas.push(name);
  });
  return areas.sort((a, b) => a.localeCompare(b, "pt-BR"));
}

/**
 * Filtra a lista com critérios combináveis (AND):
 * - status: "all" | "active" | "paused"
 * - pauseReason: "all" | "no_app_recipients" | "limit_reached" | …
 * - project / area: string vazia = todos
 * - search: texto livre (código, projeto, área, severidade)
 */
export function filterUnansweredAlerts(
  rows,
  {
    status = "all",
    pauseReason = PAUSE_REASON_FILTERS.ALL,
    project = "",
    area = "",
    search = "",
  } = {}
) {
  const list = Array.isArray(rows) ? rows : [];
  const term = normalize(search).trim();
  const projectKey = normalize(project).trim();
  const areaKey = normalize(area).trim();
  const reasonKey =
    pauseReason && pauseReason !== PAUSE_REASON_FILTERS.ALL
      ? pauseReason
      : "";

  return list.filter((row) => {
    if (status === "active" && !row.notifications_active) return false;
    if (status === "paused" && row.notifications_active) return false;

    if (reasonKey) {
      if (row.notifications_active) return false;
      if (row.notifications_pause_reason !== reasonKey) return false;
    }

    if (projectKey && normalize(row.project_title) !== projectKey) return false;
    if (areaKey && normalize(row.area_name) !== areaKey) return false;

    if (!term) return true;
    return [row.cod_alerta, row.project_title, row.area_name, row.severity]
      .map(normalize)
      .some((value) => value.includes(term));
  });
}
